import { Box, Button, FormHelperText } from '@mui/material'
import React, { useMemo, useState } from 'react'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import FormLabel from '@mui/material/FormLabel'
import TextField from '@mui/material/TextField'
import FormControl from '@mui/material/FormControl'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import { useForm, Controller } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { LoadingButton } from '@mui/lab'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import { ApiEndPoints } from 'src/network/endpoints'
import { toastError, toastSuccess } from 'src/utils/utils'
import Logo from 'src/@core/components/logo'
import { useNavigate, useSearchParams } from 'react-router-dom'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import axios from 'axios'
import { useTranslation } from 'react-i18next'

const BoxWrapper = styled(Box)(({ theme }) => ({
  [theme.breakpoints.down('xl')]: {
    width: '100%'
  },
  [theme.breakpoints.down('md')]: {
    maxWidth: 500
  }
}))

const validationSchema = yup.object().shape({
  password: yup.string().required('new_password_is_required'),
  confirm_password: yup
    .string()
    .required('please_confirm_your_new_password')
    .oneOf([yup.ref('password')], 'passwords_must_match')
})

const SetPasswordPage = () => {
  const { t } = useTranslation()
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = useMemo(() => (searchParams.get('token') || '').trim(), [searchParams])

  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onChange'
  })

  const onSubmit = async data => {
    if (!token) {
      toastError(t('auth.setPassword.invalidLink'))
      navigate('/forgot-password')
      return
    }

    setLoading(true)
    try {
      const payload = { token, password: data.password }
      const response = await axios.post(ApiEndPoints.AUTH.set_password, payload)
      toastSuccess(response?.data?.message || t('auth.setPassword.success'))
      navigate('/login')
    } catch (error) {
      setLoading(false)
      toastError(error)
    }
  }

  const handleNavigate = () => {
    navigate('/login')
  }

  return (
    <>
      <Box sx={{ p: { md: 12, xs: 8 } }}>
        <Button
          variant='outlined'
          onClick={handleNavigate}
          sx={{
            borderColor: '#F2F4F7',
            borderWidth: '2px',
            color: '#000000',
            padding: '14px 16px',
            fontWeight: 800
          }}
          startIcon={<ArrowBackRoundedIcon />}
        >
          {t('form.Back')}
        </Button>
      </Box>
      <Box
        sx={{
          p: { md: 12, xs: 8 },
          height: '80vh',
          display: 'flex',
          alignItems: 'center',
          minWidth: { xs: 'auto', sm: '670px' },
          justifyContent: 'start',
          backgroundColor: 'background.paper',
          borderRadius: '20px'
        }}
      >
        <BoxWrapper sx={{ width: '100%' }}>
          <Box sx={{ mb: 6 }} gap={4} display='flex' alignItems='start' justifyContent='start' flexDirection='column'>
            <Logo />
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '13px' }}>
              <Typography variant='h4' color='neutral.80' fontWeight={700}>
                {t('auth.setPassword.title')}
              </Typography>
              <Typography variant='body2' color='neutral.70' fontWeight={500}>
                {t('auth.setPassword.subtitle')}
              </Typography>
            </Box>
          </Box>
          <form autoComplete='off' onSubmit={handleSubmit(onSubmit)}>
            <FormControl fullWidth sx={{ mb: 4 }}>
              <FormLabel htmlFor='password'>
                <Typography variant='body2' color='neutral.80' fontWeight={600} mb={2}>
                  {t('form.NewPassword.label')}
                </Typography>
              </FormLabel>
              <Controller
                name='password'
                control={control}
                render={({ field: { value, onChange } }) => (
                  <TextField
                    autoFocus
                    onChange={onChange}
                    id='password'
                    type={showPassword ? 'text' : 'password'}
                    value={value}
                    placeholder={t('form.NewPassword.placeholder')}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position='end'>
                          <IconButton onClick={() => setShowPassword(!showPassword)} edge='start'>
                            {showPassword ? <Visibility fontSize='small' /> : <VisibilityOff fontSize='small' />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                )}
              />
              {errors.password && <FormHelperText sx={{ color: 'error.main' }}>{errors.password.message}</FormHelperText>}
            </FormControl>
            <FormControl fullWidth sx={{ mb: 4 }}>
              <FormLabel htmlFor='confirm_password'>
                <Typography variant='body2' color='neutral.80' fontWeight={600} mb={2}>
                  {t('form.ConfirmPassword.label')}
                </Typography>
              </FormLabel>
              <Controller
                name='confirm_password'
                control={control}
                render={({ field: { value, onChange } }) => (
                  <TextField
                    onChange={onChange}
                    id='confirm_password'
                    value={value}
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder={t('form.ConfirmPassword.placeholder')}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position='end'>
                          <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge='start'>
                            {showConfirmPassword ? <Visibility fontSize='small' /> : <VisibilityOff fontSize='small' />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                )}
              />
              {errors.confirm_password && (
                <FormHelperText sx={{ color: 'error.main' }}>{errors.confirm_password.message}</FormHelperText>
              )}
            </FormControl>
            <LoadingButton
              sx={{ borderRadius: '10px', height: '48px', fontWeight: 600, mt: 3 }}
              fullWidth
              loading={loading}
              size='large'
              type='submit'
              variant='contained'
            >
              {t('auth.setPassword.submit')}
            </LoadingButton>
          </form>
        </BoxWrapper>
      </Box>
    </>
  )
}

export default SetPasswordPage
